import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  adjustmentSchema,
  defaultAdjustments,
  type Adjustments,
  type Field,
  type BackgroundRemoval,
  type BackgroundPoint,
  type BackgroundRemovalMode,
  backgroundRemovalMethods,
} from "@proof/core";
import type { FormGeneratorProps } from "./FormGenerator";
import type { BackgroundRemovalService } from "./background-remover";
import { AssetImage } from "./AssetImage";
import { ImageLayer, type ImagePreviewStatus } from "./ImageLayer";
import { ImageCropSelection } from "./ImageCropSelection";
import {
  checkedPreviewBlob,
  type AssetLoader,
  type ImageTransformer,
} from "./images";

export function AdjustedImage({
  assetId,
  adjustments,
  assetUrl,
  loadAsset,
  transformImage,
  alt,
  className,
}: {
  assetId: string;
  adjustments?: Adjustments;
  assetUrl: (id: string) => string;
  loadAsset?: AssetLoader;
  transformImage?: ImageTransformer;
  alt: string;
  className?: string;
}) {
  const [attempt, setAttempt] = useState(0),
    [status, setStatus] = useState<ImagePreviewStatus>();
  const revision = useMemo(() => ({}), [assetId, attempt]);
  const changed = useCallback(
    (_id: string, _signature: string, next: ImagePreviewStatus) =>
      setStatus(next),
    [],
  );
  return (
    <>
      <ImageLayer
        node={{
          id: "image-editor-preview",
          assetId,
          adjustments,
          width: 480,
          height: 360,
        }}
        assetUrl={assetUrl}
        loadAsset={loadAsset}
        transformImage={transformImage}
        revision={revision}
        onStatus={changed}
        alt={alt}
        className={className}
      />
      {status?.error && (
        <button type="button" onClick={() => setAttempt((n) => n + 1)}>
          Retry {alt}
        </button>
      )}
    </>
  );
}

/** Edits always describe the immutable source, never a previously transformed preview. */
export function ImageAdjustmentDialog({
  field,
  assetId,
  initial,
  readOnly,
  assetUrl,
  loadAsset,
  transformImage,
  size,
  backgroundRemoval,
  removeBackground,
  upload,
  onSave,
  onClose,
}: {
  field: Field;
  assetId: string;
  initial: Adjustments;
  readOnly: boolean;
  assetUrl: (id: string) => string;
  loadAsset?: AssetLoader;
  transformImage?: ImageTransformer;
  size?: { width: number; height: number };
  backgroundRemoval?: BackgroundRemoval;
  removeBackground?: BackgroundRemovalService;
  upload?: FormGeneratorProps["upload"];
  onSave: (draft: Adjustments, background?: BackgroundRemoval) => void;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null),
    heading = useId(),
    description = useId();
  const [draft, setDraft] = useState(() => structuredClone(initial));
  const [dimensions, setDimensions] = useState(size);
  const [error, setError] = useState("");
  const [background, setBackground] = useState<BackgroundRemoval | undefined>(
    () =>
      backgroundRemoval ||
      (initial.backgroundMaskAssetId
        ? {
            version: 1,
            method: "cutaway-isnet-quint8",
            assetId: initial.backgroundMaskAssetId,
          }
        : undefined),
  );
  const [mode, setMode] = useState<BackgroundRemovalMode>(() =>
    backgroundRemoval && backgroundRemoval.method !== "solid-colour-v1"
      ? "photo"
      : "solid",
  );
  const [tolerance, setTolerance] = useState(
    () => backgroundRemoval?.tolerance ?? 24,
  );
  const [points, setPoints] = useState<BackgroundPoint[]>(
    () => backgroundRemoval?.points ?? [],
  );
  const [removing, setRemoving] = useState(false);
  const [progress, setProgress] = useState("");
  const pending = useRef<AbortController | undefined>(undefined);
  const cancelRemoval = () => {
    pending.current?.abort();
    pending.current = undefined;
    setRemoving(false);
    setProgress("");
  };
  const dismiss = () => {
    cancelRemoval();
    dialog.current?.close();
    onClose();
  };
  useEffect(() => {
    const element = dialog.current;
    if (element && !element.open) element.showModal();
    return () => {
      pending.current?.abort();
      pending.current = undefined;
      if (element?.open) element.close();
    };
  }, []);
  const adjust = (key: keyof Adjustments, value: unknown) => {
    if (!readOnly) {
      setError("");
      setDraft((current) => ({ ...current, [key]: value }));
    }
  };
  const allowed = (tool: Field["image"]["tools"][number]) =>
    field.image.tools.includes(tool);
  const removeSubjectBackground = async (picked = points) => {
    if (
      readOnly ||
      !allowed("removeBackground") ||
      !removeBackground ||
      !upload ||
      pending.current
    )
      return;
    const controller = new AbortController();
    pending.current = controller;
    setRemoving(true);
    setError("");
    setProgress("Loading original image…");
    try {
      const original = loadAsset
        ? await loadAsset(assetId, controller.signal)
        : await fetch(assetUrl(assetId), { signal: controller.signal }).then(
            (response) => {
              if (!response.ok)
                throw new Error("Original image is unavailable.");
              return response.blob();
            },
          );
      controller.signal.throwIfAborted();
      const result = checkedPreviewBlob(
        await removeBackground(
          checkedPreviewBlob(original),
          controller.signal,
          (message) => {
            if (!controller.signal.aborted) setProgress(message);
          },
          mode === "solid"
            ? { assetId, mode, tolerance, points: picked }
            : { assetId, mode },
        ),
      );
      controller.signal.throwIfAborted();
      if (result.type !== "image/png" || result.size > field.image.maxBytes)
        throw new Error(
          "The transparent image exceeds this variable's upload limit.",
        );
      const before = await createImageBitmap(original);
      const sourceSize = { width: before.width, height: before.height };
      before.close();
      const after = await createImageBitmap(result);
      const sameSize =
        after.width === sourceSize.width && after.height === sourceSize.height;
      after.close();
      if (!sameSize)
        throw new Error(
          "Background removal must preserve the original image dimensions.",
        );
      controller.signal.throwIfAborted();
      setProgress("Storing transparent image…");
      const stored = await upload(
        new File([result], `${field.label}-cutout.png`, { type: "image/png" }),
        field,
        controller.signal,
      );
      controller.signal.throwIfAborted();
      if (
        !/^asset_[a-zA-Z0-9_-]+$/.test(stored.id) ||
        stored.width !== sourceSize.width ||
        stored.height !== sourceSize.height
      )
        throw new Error("The image service returned an invalid image.");
      setBackground({
        version: 1,
        method: backgroundRemovalMethods[mode],
        assetId: stored.id,
        ...(mode === "solid"
          ? { tolerance, ...(picked.length ? { points: picked } : {}) }
          : {}),
      });
      setPoints(mode === "solid" ? picked : []);
      setDraft((current) => ({ ...current, removeBackground: false }));
    } catch (error) {
      if (!controller.signal.aborted)
        setError(
          error instanceof Error
            ? error.message
            : "Background removal failed. Please retry.",
        );
    } finally {
      if (pending.current === controller) {
        pending.current = undefined;
        setRemoving(false);
        setProgress("");
      }
    }
  };
  return (
    <dialog
      ref={dialog}
      className="proof-image-editor"
      aria-labelledby={heading}
      aria-describedby={description}
      onCancel={(event) => {
        event.stopPropagation();
        event.preventDefault();
        dismiss();
      }}
      onClose={(event) => {
        event.stopPropagation();
        if (!event.currentTarget.open) onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === "Escape") event.stopPropagation();
        if (event.key === "Enter" && event.target instanceof HTMLInputElement)
          event.preventDefault();
      }}
    >
      <style>{imageEditorStyles}</style>
      <header className="proof-image-editor-header">
        <div>
          <p className="proof-image-editor-eyebrow">{field.label}</p>
          <h2 id={heading}>{readOnly ? "View image" : "Edit image"}</h2>
          <p id={description}>
            {readOnly
              ? "This image is controlled by the document or connected data."
              : "Adjust your image, then save it to update your document."}
          </p>
        </div>
        <button type="button" aria-label="Close image editor" onClick={dismiss}>
          ×
        </button>
      </header>
      <div
        className={`proof-image-editor-layout ${field.image.tools.length === 1 && allowed("crop") ? "proof-image-editor-only-crop" : ""}`}
      >
        <section
          className="proof-image-editor-preview"
          aria-label="Image previews"
        >
          {allowed("crop") && (
            <ImageCropSelection
              assetId={assetId}
              assetUrl={assetUrl}
              loadAsset={loadAsset}
              crop={draft.crop}
              onChange={(crop) => adjust("crop", crop)}
              disabled={readOnly || removing}
              aspect={field.image.fixedAspect ? field.image.aspect : undefined}
              size={dimensions}
              onDimensions={setDimensions}
            />
          )}
          <h3>Edited preview</h3>
          <div className="proof-image-editor-stage">
            <AdjustedImage
              assetId={assetId}
              adjustments={{
                ...draft,
                backgroundMaskAssetId: background?.assetId,
              }}
              assetUrl={assetUrl}
              loadAsset={loadAsset}
              transformImage={transformImage}
              alt={`${field.label} edited preview`}
            />
          </div>
          <div
            className="proof-image-editor-original"
            onLoadCapture={(event) => {
              const image = event.target;
              if (
                image instanceof HTMLImageElement &&
                image.naturalWidth > 0 &&
                image.naturalHeight > 0
              )
                setDimensions({
                  width: image.naturalWidth,
                  height: image.naturalHeight,
                });
            }}
          >
            <AssetImage
              assetId={assetId}
              assetUrl={assetUrl}
              loadAsset={loadAsset}
              alt={`${field.label} original upload`}
            />
            <div>
              <strong>Original upload</strong>
              <p>
                Your original stays intact. Edit again at any time, or reset to
                start over.
              </p>
            </div>
          </div>
        </section>
        <section
          className="proof-image-editor-controls"
          aria-label="Image adjustments"
        >
          <h3>Image adjustments</h3>
          {allowed("removeBackground") && (
            <div className="proof-background-remover">
              <fieldset className="proof-background-mode">
                <legend>Background</legend>
                <label>
                  <input
                    type="radio"
                    name={`${heading}-background-mode`}
                    checked={mode === "solid"}
                    disabled={readOnly || removing}
                    onChange={() => setMode("solid")}
                  />
                  Solid or white background (logos, product shots)
                </label>
                <label>
                  <input
                    type="radio"
                    name={`${heading}-background-mode`}
                    checked={mode === "photo"}
                    disabled={readOnly || removing}
                    onChange={() => setMode("photo")}
                  />
                  Photo subject (people, busy backgrounds)
                </label>
              </fieldset>
              {mode === "solid" && (
                <label>
                  Tolerance
                  <input
                    aria-label="Background tolerance"
                    type="range"
                    min="1"
                    max="100"
                    value={tolerance}
                    disabled={readOnly || removing}
                    onChange={(event) =>
                      setTolerance(Number(event.target.value))
                    }
                  />
                  <output>{tolerance}</output>
                </label>
              )}
              <button
                type="button"
                disabled={readOnly || removing || !removeBackground || !upload}
                onClick={() => void removeSubjectBackground()}
              >
                {background ? "Remove background again" : "Remove background"}
              </button>
              <p>
                Background removal runs on the proof server. Save image keeps
                the transparent result; Reset edits restores the original.
              </p>
              {mode === "solid" && background && !readOnly && (
                <div
                  className="proof-background-picker"
                  onClick={(event) => {
                    const image = event.target;
                    if (
                      !(image instanceof HTMLImageElement) ||
                      removing ||
                      points.length >= 24
                    )
                      return;
                    const box = image.getBoundingClientRect();
                    if (!box.width || !box.height) return;
                    const clamp = (value: number) =>
                      Math.min(1, Math.max(0, value));
                    void removeSubjectBackground([
                      ...points,
                      {
                        x: clamp((event.clientX - box.left) / box.width),
                        y: clamp((event.clientY - box.top) / box.height),
                      },
                    ]);
                  }}
                >
                  <p>
                    Click an area that should also be clear, such as the inside
                    of a letter or a handle.
                  </p>
                  <AssetImage
                    assetId={background.assetId}
                    assetUrl={assetUrl}
                    loadAsset={loadAsset}
                    alt={`${field.label} cutout. Click an area to clear it.`}
                    className="proof-background-pick"
                    retryable={false}
                    style={{ cursor: removing ? "progress" : "crosshair" }}
                  />
                  {points.length > 0 && (
                    <button
                      type="button"
                      disabled={removing}
                      onClick={() => void removeSubjectBackground([])}
                    >
                      Restore picked areas ({points.length})
                    </button>
                  )}
                </div>
              )}
              {!readOnly && !upload && (
                <p>
                  Connect an image upload service to save a transparent result.
                </p>
              )}
              {background && <p>Background removed</p>}
              {removing && (
                <>
                  <p role="status" aria-live="polite">
                    {progress}
                  </p>
                  <button type="button" onClick={cancelRemoval}>
                    Cancel background removal
                  </button>
                </>
              )}
            </div>
          )}
          {!field.image.tools.length && (
            <p>This variable does not allow image adjustments.</p>
          )}
          {allowed("rotate") && (
            <label>
              Rotation
              <input
                aria-label="Rotation"
                type="range"
                min="-180"
                max="180"
                value={draft.rotate}
                disabled={readOnly || removing}
                onChange={(event) =>
                  adjust("rotate", Number(event.target.value))
                }
              />
              <output>{draft.rotate}°</output>
            </label>
          )}
          {(
            [
              "flipX",
              "flipY",
              "grayscale",
              "invert",
              "removeBackground",
            ] as const
          )
            .filter(allowed)
            .map((key) => (
              <label key={key} className="proof-image-editor-toggle">
                <input
                  type="checkbox"
                  disabled={readOnly || removing}
                  checked={draft[key]}
                  onChange={(event) => adjust(key, event.target.checked)}
                />
                {key === "removeBackground"
                  ? "Remove near-white background"
                  : key}
              </label>
            ))}
          {(
            [
              { key: "brightness", min: -1, max: 1, step: 0.05 },
              { key: "contrast", min: -1, max: 1, step: 0.05 },
              { key: "exposure", min: -5, max: 5, step: 0.1 },
              { key: "saturation", min: 0, max: 2, step: 0.05 },
              { key: "hue", min: -180, max: 180, step: 1 },
              { key: "temperature", min: -100, max: 100, step: 1 },
              { key: "tint", min: -100, max: 100, step: 1 },
              { key: "sharpness", min: 0, max: 10, step: 0.1 },
              { key: "blur", min: 0, max: 100, step: 1 },
              { key: "opacity", min: 0, max: 1, step: 0.05 },
            ] as const
          )
            .filter((tool) => allowed(tool.key))
            .map((tool) => (
              <label key={tool.key}>
                {tool.key}
                <input
                  type="range"
                  disabled={readOnly || removing}
                  min={tool.min}
                  max={tool.max}
                  step={tool.step}
                  value={draft[tool.key]}
                  onChange={(event) =>
                    adjust(tool.key, Number(event.target.value))
                  }
                />
                <output>{draft[tool.key]}</output>
              </label>
            ))}
          {allowed("resize") && (
            <label>
              Output width (pixels)
              <input
                type="number"
                min="1"
                max="12000"
                disabled={readOnly || removing}
                value={draft.width ?? ""}
                onChange={(event) =>
                  adjust(
                    "width",
                    event.target.value ? Number(event.target.value) : undefined,
                  )
                }
              />
            </label>
          )}
          {error && <p role="alert">{error}</p>}
        </section>
      </div>
      <footer className="proof-image-editor-actions">
        {!readOnly && (
          <button
            type="button"
            onClick={() => {
              cancelRemoval();
              setBackground(undefined);
              setPoints([]);
              setDraft(defaultAdjustments());
              setError("");
            }}
          >
            Reset edits
          </button>
        )}
        <div>
          <button type="button" onClick={dismiss}>
            {readOnly ? "Close" : "Cancel"}
          </button>
          {!readOnly && (
            <button
              type="button"
              className="proof-image-editor-save"
              disabled={removing}
              onClick={() => {
                const parsed = adjustmentSchema.safeParse(draft);
                if (!parsed.success) {
                  setError("Review the adjustment values before saving.");
                  return;
                }
                const defaults = defaultAdjustments();
                if (
                  (background && !allowed("removeBackground")) ||
                  (Object.keys(parsed.data) as (keyof Adjustments)[]).some(
                    (key) =>
                      key !== "version" &&
                      JSON.stringify(parsed.data[key]) !==
                        JSON.stringify(defaults[key]) &&
                      !allowed(
                        key === "width"
                          ? "resize"
                          : key === "backgroundMaskAssetId"
                            ? "removeBackground"
                            : key,
                      ),
                  )
                ) {
                  setError(
                    "Some saved edits are no longer allowed for this variable. Reset edits to continue.",
                  );
                  return;
                }
                dialog.current?.close();
                const { backgroundMaskAssetId: _mask, ...saved } = parsed.data;
                onSave(saved, background);
              }}
            >
              Save image
            </button>
          )}
        </div>
      </footer>
    </dialog>
  );
}

const imageEditorStyles = `
.proof-image-editor-only-crop .proof-image-editor-controls{display:none}.proof-image-editor-layout.proof-image-editor-only-crop{display:block}@media(min-width:681px){.proof-image-editor-only-crop .proof-image-editor-preview{display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1fr);gap:12px 24px;align-items:start}.proof-image-editor-only-crop .proof-cropper{grid-column:1;grid-row:1/5;margin:0}.proof-image-editor-only-crop .proof-image-editor-preview>h3{grid-column:2;margin-top:0}.proof-image-editor-only-crop .proof-image-editor-stage,.proof-image-editor-only-crop .proof-image-editor-original{grid-column:2}.proof-image-editor-only-crop .proof-image-editor-original{margin-top:0}}
.proof-cropper{margin-bottom:20px}.proof-cropper-surface{position:relative;margin:12px auto;overflow:hidden;touch-action:none;user-select:none;cursor:crosshair;background:repeating-conic-gradient(#e7ece7 0% 25%,#fafbf9 0% 50%) 0 0/20px 20px}.proof-cropper-surface>img{display:block;width:100%;height:auto;pointer-events:none}.proof-cropper-selection{position:absolute;border:2px solid #fff;outline:1px solid #263b36;box-shadow:0 0 0 1000px #10251c88;cursor:move;min-width:1px;min-height:1px}.proof-cropper-selection:focus-visible{outline:3px solid #6392e4}.proof-cropper-grid{position:absolute;inset:0;background:linear-gradient(to right,transparent 32.8%,#ffffff80 33%,#ffffff80 33.5%,transparent 33.7%,transparent 66.1%,#ffffff80 66.3%,#ffffff80 66.8%,transparent 67%),linear-gradient(to bottom,transparent 32.8%,#ffffff80 33%,#ffffff80 33.5%,transparent 33.7%,transparent 66.1%,#ffffff80 66.3%,#ffffff80 66.8%,transparent 67%);pointer-events:none}.proof-image-editor button.proof-cropper-handle{position:absolute;width:22px;height:22px;padding:0;border:0;background:transparent;border-radius:0;transform:translate(-50%,-50%);touch-action:none}.proof-cropper-handle:after{content:"";position:absolute;inset:6px;border:1px solid #263b36;background:#fff;border-radius:2px}.proof-cropper-handle.handle-nw{left:0;top:0;cursor:nwse-resize}.proof-cropper-handle.handle-ne{left:100%;top:0;cursor:nesw-resize}.proof-cropper-handle.handle-se{left:100%;top:100%;cursor:nwse-resize}.proof-cropper-handle.handle-sw{left:0;top:100%;cursor:nesw-resize}.proof-cropper-handle.handle-n{left:50%;top:0;cursor:ns-resize}.proof-cropper-handle.handle-s{left:50%;top:100%;cursor:ns-resize}.proof-cropper-handle.handle-e{left:100%;top:50%;cursor:ew-resize}.proof-cropper-handle.handle-w{left:0;top:50%;cursor:ew-resize}.proof-cropper-surface[aria-disabled=true],.proof-cropper-selection[aria-disabled=true]{cursor:default}.proof-cropper output{display:block;color:#64736b;font-size:12px;margin-bottom:14px}
.proof-image-editor{box-sizing:border-box;width:min(980px,calc(100vw - 24px));max-width:calc(100vw - 24px);max-height:calc(100dvh - 24px);margin:auto;padding:0;border:1px solid #dfe6e1;border-radius:16px;background:#fff;color:#263b36;font:14px/1.5 system-ui,sans-serif;overflow:auto;box-shadow:0 24px 80px #122a3540}
.proof-image-editor::backdrop{background:#18332f80;backdrop-filter:blur(3px)}
.proof-image-editor *{box-sizing:border-box}.proof-image-editor p{margin:4px 0;color:#64736b;font-size:13px;line-height:1.55}
.proof-image-editor h2{font-size:24px;margin:2px 0;letter-spacing:-.5px}.proof-image-editor h3{font-size:14px;margin:0 0 12px}.proof-image-editor-header{display:flex;justify-content:space-between;gap:16px;padding:22px 24px;border-bottom:1px solid #e3e9e5}.proof-image-editor .proof-image-editor-eyebrow{font-size:11px;text-transform:uppercase;letter-spacing:1px;color:#47735d}
.proof-image-editor button{font:inherit;border:1px solid #d9e2dc;border-radius:7px;background:#fff;padding:9px 14px;cursor:pointer;color:inherit}.proof-image-editor button:focus-visible{outline:3px solid #8eaee6;outline-offset:2px}.proof-image-editor-header>button{align-self:start;font-size:23px;padding:2px 11px}
.proof-image-editor[open]{display:flex;flex-direction:column;overflow:hidden}.proof-image-editor-header,.proof-image-editor-actions{flex:none}.proof-image-editor-layout{display:grid;grid-template-columns:minmax(0,1.2fr) minmax(240px,1fr);grid-template-rows:minmax(0,1fr);flex:1 1 auto;min-height:0}.proof-image-editor-preview{min-height:0;overflow:auto}.proof-image-editor-preview{padding:24px;background:#f5f7f5;min-width:0}.proof-image-editor-stage{display:flex;align-items:center;justify-content:center;min-height:160px;height:min(360px,40dvh);border:1px solid #e0e6e1;border-radius:10px;background:repeating-conic-gradient(#e7ece7 0% 25%,#fafbf9 0% 50%) 0 0/20px 20px;overflow:hidden}.proof-image-editor-stage img{display:block;width:100%;height:100%;max-width:100%;max-height:360px;object-fit:contain}.proof-image-editor-original{display:flex;gap:12px;align-items:center;margin-top:18px}.proof-image-editor-original img{width:64px;height:64px;object-fit:contain;border:1px solid #dfe6e1;border-radius:6px;background:#fff}.proof-image-editor-original p{font-size:12px}
.proof-image-editor-controls{padding:24px;min-width:0;min-height:0;overflow:auto}.proof-image-editor-controls fieldset{padding:0;margin:0 0 14px;border:0;min-width:0}.proof-image-editor-controls legend{font-size:13px;font-weight:600}.proof-image-editor-controls label{display:grid;grid-template-columns:100px minmax(70px,1fr) 42px;align-items:center;gap:10px;margin:13px 0;font-size:12px}.proof-image-editor-controls input[type=range]{width:100%;min-width:0;padding:0;margin:0;accent-color:#47735d}.proof-image-editor-controls output{text-align:right;color:#64736b;font-size:11px}.proof-image-editor-controls label.proof-image-editor-toggle{display:flex;gap:10px}.proof-image-editor-controls input[type=checkbox]{width:auto;margin:0;accent-color:#47735d}.proof-image-editor-controls input[type=number]{width:100%;grid-column:2/4;padding:8px;border:1px solid #d9e2dc;border-radius:6px;font:inherit}
.proof-image-editor-actions{display:flex;justify-content:space-between;gap:12px;align-items:center;position:sticky;bottom:0;background:#fff;border-top:1px solid #e3e9e5;padding:16px 24px}.proof-image-editor-actions>div{display:flex;gap:8px;margin-left:auto}.proof-image-editor button.proof-image-editor-save{background:#47735d;border-color:#47735d;color:#fff}
.proof-background-mode{border:0;padding:0;margin:0 0 10px}.proof-background-mode legend{font-weight:600;padding:0}.proof-background-mode label{display:flex;gap:8px;align-items:center;margin:4px 0}.proof-background-picker{margin:10px 0}.proof-background-picker img{display:block;max-width:100%;max-height:220px;width:auto;height:auto;margin:6px 0;border:1px solid #dfe6e1;border-radius:6px;background:repeating-conic-gradient(#e7ece7 0% 25%,#fafbf9 0% 50%) 0 0/16px 16px}
.image-field-actions{display:flex;flex-wrap:wrap;gap:8px;margin-top:10px}.image-field-actions button{border:1px solid #dfe6e1;border-radius:6px;padding:7px 10px;background:#fff;font:inherit;cursor:pointer}
@media(max-width:680px){.proof-image-editor-layout{grid-template-columns:minmax(0,1fr)}.proof-image-editor-header,.proof-image-editor-preview,.proof-image-editor-controls{padding:16px}.proof-image-editor-stage{height:200px;min-height:150px;max-height:240px}.proof-image-editor-controls,.proof-image-editor-preview{overflow:visible}.proof-image-editor-layout{grid-template-rows:none;overflow:auto}.proof-image-editor-actions{padding:12px;flex-wrap:wrap}.proof-image-editor-controls label{grid-template-columns:100px minmax(55px,1fr) 36px}}
`;
